import { connect } from 'react-redux';

/* Styled Components */
import { PageFooter } from 'styles/signup/MultiScreenSignupPage';

/* Child Components */
import RadioBox from 'components/signup/multiscreen/RadioBox';
import SectionHeader from 'components/signup/multiscreen/SectionHeader';
import Button, { BUTTON_COLOR_KEYS } from 'components/common/Button';

/* Icons */
import StoreIcon from 'icons/StoreIcon';
import HomeIcon from 'icons/HomeIcon';

/* Actions */
import {
  submitSignupStep as submitSignupStepAction,
  setStoreType as setStoreTypeAction,
  setErrorMessage as setErrorMessageAction,
  clearErrorMessage as clearErrorAction,
  setCurrentSignupStep as setCurrentSignupStepAction,
} from 'app/signupSlice';

/* Selectors */
import { getStoreType } from 'app/signupSlice';
import { getDeciderKeyResult } from 'app/experimentSlice';

/* Utils */
import { i18n } from 'utils/i18n';

/* Constants */
import {
  SIGNUP_SCREEN,
  STORE_TYPE,
  CS_STORE_TYPE,
} from 'constants/SignupConstants';
import {
  ALLOW_RETAIL_SIGNUP_ONLY,
  USE_CS_STORE_TYPE,
} from 'constants/ExperimentConstants';

/* Types */
import type { AppState, AppDispatch } from 'app/store';
import type { OptionsType as SubmitSignupStepOptionsType } from 'services/SubmitSignupStepService';

const mapStateToProps = (state: AppState) => ({
  storeType: getStoreType(state),
  allowRetailSignupOnly: getDeciderKeyResult(state, ALLOW_RETAIL_SIGNUP_ONLY),
  useCSStoreType: getDeciderKeyResult(state, USE_CS_STORE_TYPE),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  submitSignupStep: (args: {
    signupStep: Values<typeof SIGNUP_SCREEN>;
    nextSignupStep: Values<typeof SIGNUP_SCREEN>;
    options: SubmitSignupStepOptionsType;
  }) => dispatch(submitSignupStepAction(args)),
  clearError: () => dispatch(clearErrorAction()),
  setErrorMessage: (errorMessage: string) =>
    dispatch(setErrorMessageAction(errorMessage)),
  setStoreType: (storeType: Values<typeof STORE_TYPE>) =>
    dispatch(setStoreTypeAction(storeType)),
  setCurrentSignupStep: (signupStep: Values<typeof SIGNUP_SCREEN>) =>
    dispatch(setCurrentSignupStepAction(signupStep)),
});

const radioOptions = () => [
  {
    index: STORE_TYPE.RETAIL,
    title: i18n('Brick and mortar store'),
    description: i18n('I sell from a retail storefront'),
    icon: (
      <StoreIcon
        overrideStyle={{
          width: 16,
          height: 16,
        }}
      />
    ),
  },
  {
    index: STORE_TYPE.HOME,
    title: i18n('Home or online only'),
    description: i18n('I sell from my home or from my own website'),
    icon: (
      <HomeIcon
        overrideStyle={{
          width: 16,
          height: 16,
        }}
      />
    ),
  },
];

type PropsType = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const StoreTypePage = ({
  setErrorMessage,
  clearError,
  submitSignupStep,
  storeType,
  allowRetailSignupOnly,
  useCSStoreType,
  setStoreType,
  setCurrentSignupStep,
}: PropsType) => {
  const onSubmit = () => {
    if (storeType === null || storeType === undefined) {
      setErrorMessage(i18n('Please select a store type'));
      return;
    }
    const nextSignupStep =
      allowRetailSignupOnly && storeType === STORE_TYPE.HOME
        ? SIGNUP_SCREEN.UNSUPPORTED_STORE_TYPE
        : SIGNUP_SCREEN.STORE_NAME;

    // TODO: After launching the dkey, come back to refactor logic
    // to use a single store type enum using the CS numbering.
    let csStoreType;
    if (useCSStoreType) {
      csStoreType =
        storeType === STORE_TYPE.RETAIL
          ? CS_STORE_TYPE.RETAIL
          : CS_STORE_TYPE.HOME;
    }
    submitSignupStep({
      signupStep: SIGNUP_SCREEN.STORE_TYPE,
      nextSignupStep,
      options: {
        storeType,
        csStoreType,
      },
    });
  };

  return (
    <>
      <SectionHeader
        title={i18n('What type of store do you have?')}
        onBackClicked={() => {
          clearError();
          setCurrentSignupStep(SIGNUP_SCREEN.CREATE_ACCOUNT);
        }}
      />

      {radioOptions().map(radioOption => (
        <RadioBox
          key={radioOption.index}
          onClick={() => {
            clearError();
            setStoreType(radioOption.index);
          }}
          selected={storeType === radioOption.index}
          title={radioOption.title}
          description={radioOption.description}
          icon={radioOption.icon}
        />
      ))}

      <PageFooter>
        <Button
          onClick={onSubmit}
          color={BUTTON_COLOR_KEYS.DARK_COBALT}
          stretch
          bold
          overrideStyle={{
            margin: '24px 0',
          }}
        >
          {i18n('Continue')}
        </Button>
      </PageFooter>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(StoreTypePage);
